import { useCallback, useEffect, useState } from 'react';

/**
 * A layout inspector for phones, where no browser devtools exist.
 *
 * Off unless the URL asks for it: add `?debug=layout` to any page (or `#debug`).
 * It never mounts otherwise, so there is no cost to normal visitors.
 *
 * The important part is `hitTest` below: it walks straight down the middle of the
 * screen asking the browser which element is actually painted at each point. When
 * a region looks blank, that answers "blank according to whom" -- which element is
 * on top there, how big it is, and what it is filled with. Reading rects alone
 * can't tell you that.
 */

const WATCH = [
  'html',
  'body',
  '#root',
  '.home',
  '.home-head',
  '.home-body',
  '.mapwrap',
  '.maplibregl-map',
  '.maplibregl-canvas',
  '.sheet',
  '.sheet-head',
  '.sheet-scroll',
  '.results',
  '.empty',
  '.plan-run',
  '.cookie-banner',
];

const STYLE_KEYS = [
  'position',
  'display',
  'height',
  'minHeight',
  'maxHeight',
  'overflowY',
  'flex',
  'transform',
  'visibility',
  'opacity',
  'zIndex',
  'backgroundColor',
] as const;

function describe(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const id = el.id ? '#' + el.id : '';
  const cls =
    typeof el.className === 'string' && el.className.trim()
      ? '.' + el.className.trim().split(/\s+/).slice(0, 3).join('.')
      : '';
  return tag + id + cls;
}

function px(n: number): string {
  return String(Math.round(n));
}

function elementReport(sel: string): string {
  const el = document.querySelector(sel);
  if (!el) return sel + ': (not present)';
  const r = el.getBoundingClientRect();
  const cs = getComputedStyle(el);
  const styles = STYLE_KEYS.map((k) => k + '=' + cs[k]).join(' ');
  return (
    sel +
    ': rect x=' +
    px(r.x) +
    ' y=' +
    px(r.y) +
    ' w=' +
    px(r.width) +
    ' h=' +
    px(r.height) +
    ' | client=' +
    px(el.clientHeight) +
    ' scroll=' +
    px(el.scrollHeight) +
    ' offset=' +
    px((el as HTMLElement).offsetHeight ?? 0) +
    '\n    ' +
    styles
  );
}

/** Which element is actually on top, straight down the centre of the screen. */
function hitTest(): string {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const lines: string[] = [];
  for (let pct = 5; pct <= 97; pct += 8) {
    const y = Math.round((h * pct) / 100);
    const el = document.elementFromPoint(Math.round(w / 2), y);
    if (!el) {
      lines.push('  y=' + y + ' (' + pct + '%): nothing');
      continue;
    }
    const r = el.getBoundingClientRect();
    const cs = getComputedStyle(el);
    lines.push(
      '  y=' +
        y +
        ' (' +
        pct +
        '%): ' +
        describe(el) +
        ' h=' +
        px(r.height) +
        ' bg=' +
        cs.backgroundColor,
    );
  }
  return lines.join('\n');
}

function build(): string {
  const vv = window.visualViewport;
  const out: string[] = [];
  out.push('--- theyardsalemap layout report ---');
  out.push('url: ' + location.href);
  out.push('ua: ' + navigator.userAgent);
  out.push(
    'window: inner=' +
      window.innerWidth +
      'x' +
      window.innerHeight +
      ' outer=' +
      window.outerWidth +
      'x' +
      window.outerHeight +
      ' dpr=' +
      window.devicePixelRatio,
  );
  out.push(
    'screen: ' +
      screen.width +
      'x' +
      screen.height +
      ' avail=' +
      screen.availWidth +
      'x' +
      screen.availHeight,
  );
  out.push(
    'visualViewport: ' +
      (vv
        ? px(vv.width) +
          'x' +
          px(vv.height) +
          ' offsetTop=' +
          px(vv.offsetTop) +
          ' scale=' +
          vv.scale
        : '(unsupported)'),
  );
  out.push(
    'css support: dvh=' +
      String(CSS.supports('height', '100dvh')) +
      ' svh=' +
      String(CSS.supports('height', '100svh')) +
      ' webgl=' +
      String(!!document.querySelector('.maplibregl-canvas')),
  );
  out.push('scroll: x=' + px(window.scrollX) + ' y=' + px(window.scrollY));
  out.push('');
  out.push('== elements ==');
  for (const sel of WATCH) out.push(elementReport(sel));
  out.push('');
  out.push('== what is painted down the centre ==');
  out.push(hitTest());
  return out.join('\n');
}

export function DebugPanel() {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(true);
  const [copied, setCopied] = useState('');

  const refresh = useCallback(() => {
    setText(build());
    setCopied('');
  }, []);

  useEffect(() => {
    // Let layout and the map settle before the first snapshot.
    const t = setTimeout(refresh, 600);
    window.addEventListener('resize', refresh);
    window.addEventListener('orientationchange', refresh);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', refresh);
      window.removeEventListener('orientationchange', refresh);
    };
  }, [refresh]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied('Copied.');
    } catch {
      // Clipboard API needs a secure context and can still be refused; the
      // textarea below is the fallback -- select all in it and copy by hand.
      setCopied('Copy blocked. Select the text below instead.');
    }
  }

  if (!open) {
    return (
      <button type="button" className="dbg-reopen" onClick={() => setOpen(true)}>
        debug
      </button>
    );
  }

  return (
    <div className="dbg">
      <div className="dbg-bar">
        <strong>layout report</strong>
        <button type="button" onClick={refresh}>
          refresh
        </button>
        <button type="button" onClick={() => void copy()}>
          copy
        </button>
        <button type="button" onClick={() => setOpen(false)}>
          hide
        </button>
      </div>
      {copied && <p className="dbg-msg">{copied}</p>}
      <textarea className="dbg-out" readOnly value={text} spellCheck={false} />
    </div>
  );
}

export function debugRequested(): boolean {
  return /(^|[?&])debug=layout($|&)/.test(location.search) || location.hash === '#debug';
}
